import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { X, Save, Trash2, Link2, AlertCircle, FileText, GripVertical, Maximize2, Minimize2 } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import NewExerciseSelector from '../../common/NewExerciseSelector';
import ImageWithPreview from '../../common/ImageWithPreview';
import ImprovedUserExerciseForm from '../../Standalone/ImprovedUserExerciseForm';
import usePaginatedExercises from '../../../hooks/usePaginatedExercises';
import RecoveryMinSecInputs from '../common/RecoveryMinSecInputs';
import PyramidSeriesEditor from '../common/PyramidSeriesEditor';
import TemplateRepsInputs from '../common/TemplateRepsInputs';
import { GYM_WORKOUT_SET_TYPE_OPTIONS } from '../../../constants/workoutSetTypes';
import { buildDefaultSeriesPlan, isPyramidExercise, parseSeriesPlan } from '../../../constants/workoutSeriesPlan';
import { buildTemplateRepsPayload, inferTemplateRepsMode } from '../../../constants/templateRepsMode';

const createClientRowId = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? `new-${crypto.randomUUID()}`
    : `new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
);

const catalogIdFromRow = (ex) => {
  if (ex.esercizio_id != null && ex.esercizio_id !== '') return Number(ex.esercizio_id);
  if (typeof ex.id === 'number') return ex.id;
  return null;
};

// Componente locale per esercizio ordinabile con drag & drop
const SortableExerciseItem = ({ exercise, index, onUpdate, onRemove, selectedExercises }) => {
  const rowId = exercise.id || `exercise-${index}`;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowId });

  // Stato locale per il campo recupero (permette campo vuoto durante digitazione)
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isCardio = exercise.is_cardio === 1 || exercise.is_cardio === true;
  const catalogId = catalogIdFromRow(exercise);
  const duplicateCount = catalogId != null
    ? selectedExercises.filter((e) => catalogIdFromRow(e) === catalogId).length
    : 1;
  const ordinal = catalogId != null
    ? selectedExercises.slice(0, index + 1).filter((e) => catalogIdFromRow(e) === catalogId).length
    : 1;
  const nameSuffix = duplicateCount > 1 ? ` (${ordinal}°)` : '';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-4 rounded-lg border ${
        exercise.linked_to_previous
          ? 'bg-green-50 dark:bg-green-900/20 border-green-400 border-l-4 ml-4'
          : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-white/10'
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab hover:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <span className="text-sm font-medium text-gray-500 dark:text-white/65">
            {index + 1}.
          </span>
          {/* Immagine esercizio con preview hover */}
          {(exercise.immagine_nome || exercise.immagine_statica) ? (
            <div className="w-10 h-10 rounded overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0">
              <ImageWithPreview
                src={exercise.immagine_nome ? `/uploads/images/${exercise.immagine_nome}` : exercise.immagine_statica}
                alt={exercise.nome || exercise.exercise_name}
                maxHeight="h-10"
                maxWidth="w-10"
                previewSize="w-80"
              />
            </div>
          ) : (
            <div className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
              <span className="text-gray-400 text-xs">📷</span>
            </div>
          )}
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">
              {exercise.nome || exercise.exercise_name}{nameSuffix}
            </h4>
            <p className="text-sm text-gray-500 dark:text-white/65">
              {exercise.gruppo_muscolare}
            </p>
          </div>
        </div>
        
        <button
          onClick={() => onRemove(index)}
          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {isCardio ? (
        // Layout per esercizi cardio: Serie, Minuti
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500 dark:text-white/65">Serie</label>
            <input
              type="number"
              min="1"
              value={exercise.serie || 1}
              onChange={(e) => onUpdate(index, 'serie', parseInt(e.target.value) || 1)}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
          
          <div>
            <label className="text-xs text-gray-500 dark:text-white/65">Minuti</label>
            <input
              type="number"
              min="0"
              placeholder="20"
              value={exercise.ripetizioni_max}
              onChange={(e) => onUpdate(index, 'ripetizioni_max', parseInt(e.target.value) || 0)}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      ) : !isPyramidExercise(exercise) ? (
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-xs text-gray-500 dark:text-white/65">Serie</label>
            <input
              type="number"
              min="1"
              value={exercise.serie ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                onUpdate(index, 'serie', value === '' ? '' : parseInt(value) || '');
              }}
              onBlur={(e) => {
                if (e.target.value === '' || e.target.value === '0') {
                  onUpdate(index, 'serie', 1);
                }
              }}
              onFocus={(e) => e.target.select()}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>

          <TemplateRepsInputs
            exercise={exercise}
            onUpdate={(field, value) => onUpdate(index, field, value)}
          />

          <div>
            <label className="text-xs text-gray-500 dark:text-white/65">Peso (kg)</label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={exercise.peso_suggerito ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                onUpdate(index, 'peso_suggerito', value === '' ? '' : parseFloat(value) || '');
              }}
              onBlur={(e) => {
                if (e.target.value === '') {
                  onUpdate(index, 'peso_suggerito', 0);
                }
              }}
              onFocus={(e) => e.target.select()}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-3 gap-2 mt-2">
        <RecoveryMinSecInputs
          compact
          layout="templateGrid"
          templateFieldSize="sm"
          gymDarkInputs
          valueSeconds={exercise.tempo_recupero}
          onSecondsChange={(sec) => onUpdate(index, 'tempo_recupero', sec)}
          defaultTotalSeconds={90}
          focusColor="indigo"
        />

        <div className="min-w-0">
          <label className="text-xs text-gray-500 dark:text-white/65 block">Tipo</label>
          <select
            value={exercise.set_type}
            onChange={(e) => onUpdate(index, 'set_type', e.target.value)}
            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            {GYM_WORKOUT_SET_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <PyramidSeriesEditor
        exercise={{ ...exercise, id: rowId, ripetizioni: exercise.ripetizioni_max, peso: exercise.peso_suggerito }}
        onUpdate={(_id, field, value) => onUpdate(index, field, value)}
        gymDarkInputs
      />

      {/* Campo Note Trainer */}
      <div className="mt-2">
        <label className="text-xs text-gray-500 dark:text-white/65">📝 Note Trainer</label>
        <textarea
          value={exercise.notes?.trainer ?? ''}
          onChange={(e) => {
            // Mantieni le note esistenti (user, system) quando si modifica trainer
            const existingNotes = exercise.notes || { trainer: null, user: null, system: null };
            const newNotes = {
              trainer: e.target.value || null,
              user: existingNotes.user || null,
              system: existingNotes.system || null
            };
            onUpdate(index, 'notes', newNotes);
          }}
          rows={2}
          maxLength={500}
          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white mt-1"
          placeholder="Aggiungi istruzioni per l'utente (es: 'Mantieni la schiena dritta', 'Arrivare al massimo')"
        />
      </div>

      {/* WEBAPP-004: Checkbox Isometrico (solo se NON è cardio) */}
      {!isCardio && (
        <div className="mt-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={exercise.is_isometric_scheda || false}
              onChange={(e) => onUpdate(index, 'is_isometric_scheda', e.target.checked)}
              className="h-4 w-4 text-indigo-600 rounded"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-white/85">
              ⏱️ Esercizio Isometrico (mostra secondi invece di ripetizioni)
            </span>
          </label>
        </div>
      )}

      {index > 0 && 
       exercise.set_type !== 'normal' && 
       exercise.set_type && 
       selectedExercises[index - 1]?.set_type === exercise.set_type && (
        <div className="mt-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={exercise.linked_to_previous}
              onChange={(e) => onUpdate(index, 'linked_to_previous', e.target.checked)}
              className="h-4 w-4 text-indigo-600 rounded"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-white/85">
              <Link2 className="h-3 w-3 inline mr-1" />
              Collega all'esercizio precedente ({exercise.set_type})
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

export default function EditTemplateModal({ isOpen, template, onClose, onSuccess }) {
  const { getAuthenticatedApi } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const api = getAuthenticatedApi();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    difficulty_level: 'intermediate',
    target_goal: 'general',
    estimated_duration: 45,
    notes: ''
  });

  const [selectedExercises, setSelectedExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [showAddExerciseForm, setShowAddExerciseForm] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false); // WEBAPP-GYM-004: Stato fullscreen
  
  // State per filtri avanzati (come in archivio esercizi)
  const [filters, setFilters] = useState({
    equipment: '',
    primaryMuscle: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // WEBAPP-021: Usa hook paginato invece di caricamento completo
  const {
    exercises: availableExercises,
    loading: loadingExercises,
    initialLoading,
    hasMore,
    loadMore,
    search: searchExercises,
    filterByGroup,
    refresh: refreshExercises
  } = usePaginatedExercises({
    initialLimit: 50,
    enableInfiniteScroll: true
  });
  
  // Gestisce il cambio filtri da NewExerciseSelector
  const handleFiltersChange = useCallback((newFilters) => {
    const newEquipment = newFilters.equipment || '';
    const newPrimaryMuscle = newFilters.primaryMuscle || '';
    
    setFilters({
      equipment: newEquipment,
      primaryMuscle: newPrimaryMuscle
    });
    
    // Applica i filtri tramite searchExercises solo se c'è almeno un filtro attivo o ricerca
    const hasFilters = newEquipment || newPrimaryMuscle;
    const hasSearch = searchTerm && searchTerm.trim().length >= 2;
    
    if (hasFilters || hasSearch) {
      searchExercises(hasSearch ? searchTerm : '', {
        equipment: newEquipment && newEquipment.trim() !== '' ? newEquipment.trim() : undefined,
        primaryMuscle: newPrimaryMuscle && newPrimaryMuscle.trim() !== '' ? newPrimaryMuscle.trim() : undefined
      });
    } else {
      // WEBAPP-GYM-011: reset filtri senza testo ricerca → ricarica elenco completo
      searchExercises('', {});
    }
  }, [searchExercises, searchTerm]);
  
  // Gestisce la ricerca testuale
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
    
    // Applica ricerca con filtri attivi solo se c'è almeno ricerca (>=2 caratteri) o filtri
    const hasSearch = term && term.trim().length >= 2;
    const hasFilters = filters.equipment || filters.primaryMuscle;
    
    if (hasSearch || hasFilters) {
      searchExercises(hasSearch ? term.trim() : '', {
        equipment: filters.equipment && filters.equipment.trim() !== '' ? filters.equipment.trim() : undefined,
        primaryMuscle: filters.primaryMuscle && filters.primaryMuscle.trim() !== '' ? filters.primaryMuscle.trim() : undefined
      });
    }
  }, [searchExercises, filters]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (isOpen && template) {
      loadTemplateData();
      // WEBAPP-021: L'hook usePaginatedExercises gestisce già il caricamento iniziale
    }
  }, [isOpen, template]);

  const loadTemplateData = async () => {
    try {
      setLoadingData(true);
      
      // Carica dettagli completi se non già presenti
      let fullTemplate = template;
      if (!template.exercises) {
        const response = await api.get(`/gym_workout_templates.php?action=get&id=${template.id}`);
        if (response.data.success) {
          fullTemplate = response.data.template;
        }
      }

      setFormData({
        name: fullTemplate.name || '',
        description: fullTemplate.description || '',
        difficulty_level: fullTemplate.difficulty_level || 'intermediate',
        target_goal: fullTemplate.target_goal || 'general',
        estimated_duration: fullTemplate.estimated_duration || 45,
        notes: fullTemplate.notes || ''
      });

      if (fullTemplate.exercises) {
        // Carica anche gli esercizi completi per avere is_cardio
        const exercisesWithDetails = await Promise.all(
          fullTemplate.exercises.map(async (ex) => {
            try {
              const exResponse = await api.get(`/esercizi.php?id=${ex.esercizio_id}`);
              return exResponse.data;
            } catch {
              return null;
            }
          })
        );
        
        setSelectedExercises(fullTemplate.exercises.map((ex, index) => {
          const exerciseDetails = exercisesWithDetails[index];
          const isCardio = exerciseDetails?.is_cardio === 1 || exerciseDetails?.is_cardio === true;
          
          // Usa ripetizioni_max come valore principale (o ripetizioni_min se max non esiste per retrocompatibilità)
          let ripetizioni_max = ex.ripetizioni_max || ex.ripetizioni_min || (isCardio ? 20 : 12);
          
          if (isCardio && ripetizioni_max) {
            // Converti secondi in minuti per l'UI (il DB salva in secondi)
            ripetizioni_max = Math.round(ripetizioni_max / 60);
          }
          
          // Carica note come JSON (formato: {"trainer": "...", "user": null, "system": null})
          let notes = { trainer: null, user: null, system: null };
          if (ex.notes) {
            if (typeof ex.notes === 'string' && ex.notes.trim() !== '') {
              // Se è una stringa, prova a parsare come JSON
              if (ex.notes.trim().startsWith('{')) {
                try {
                  notes = JSON.parse(ex.notes);
                } catch (e) {
                  // Se non è JSON valido, considera come note trainer legacy
                  notes = { trainer: ex.notes.trim(), user: null, system: null };
                }
              } else {
                // Stringa semplice → considera come note trainer legacy
                notes = { trainer: ex.notes.trim(), user: null, system: null };
              }
            } else if (typeof ex.notes === 'object' && ex.notes !== null) {
              // Se è già un oggetto, usalo direttamente
              notes = ex.notes;
            }
          }
          
          return {
            ...ex,
            ...exerciseDetails,
            id: `te-${ex.id}`,
            esercizio_id: ex.esercizio_id,
            nome: ex.nome || ex.exercise_name || exerciseDetails?.nome,
            order_index: index,
            set_type: (ex.set_type && ex.set_type.trim() !== '') ? ex.set_type.trim() : 'normal',
            series_plan: parseSeriesPlan(ex.series_plan),
            ripetizioni_min: ex.ripetizioni_min ?? 0,
            ripetizioni_max: ripetizioni_max,
            reps_mode: inferTemplateRepsMode({ ...ex, ripetizioni_min: ex.ripetizioni_min, ripetizioni_max: ex.ripetizioni_max }),
            is_isometric_scheda: ex.is_isometric_scheda || false,
            notes: notes
          };
        }));
      }
    } catch (error) {
      console.error('Errore nel caricamento template:', error);
    } finally {
      setLoadingData(false);
    }
  };

  // WEBAPP-021: Rimossa fetchExercises - ora usiamo usePaginatedExercises

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectExercise = (exercise) => {
    const catalogId = exercise.esercizio_id || exercise.id;
    if (!catalogId) {
      toast.error('Esercizio senza ID valido');
      return;
    }

    const isCardio = exercise.is_cardio === 1 || exercise.is_cardio === true;

    setSelectedExercises(prev => [
      ...prev,
      {
        ...exercise,
        id: createClientRowId(),
        esercizio_id: catalogId,
        serie: isCardio ? 1 : 3,
        ripetizioni_max: isCardio ? 20 : 12,
        peso_suggerito: isCardio ? 0 : 0,
        tempo_recupero: 90,
        set_type: 'normal',
        linked_to_previous: false,
        order_index: prev.length,
        notes: { trainer: null, user: null, system: null },
      },
    ]);
  };

  const handleExerciseCreated = async (result) => {
    // result contiene {success: true, message: '...', exercise_id: 293}
    if (result.success && result.exercise_id) {
      try {
        // Recupera l'esercizio completo dal backend usando l'ID specifico
        const response = await api.get(`/esercizi.php?id=${result.exercise_id}`);
        
        if (response.data && response.data.id) {
          const newExercise = response.data;
          
          // WEBAPP-021: Ricarica lista esercizi per includere il nuovo
          refreshExercises();
          
          // Seleziona automaticamente l'esercizio appena creato
          handleSelectExercise(newExercise);
          
          // Chiudi il form
          setShowAddExerciseForm(false);
          
          toast.success('Esercizio creato e aggiunto al template!');
        }
      } catch (error) {
        console.error('Errore nel recupero esercizio creato:', error);
        toast.error('Esercizio creato ma errore nel recupero');
      }
    }
  };

  const updateExercise = (index, field, value) => {
    setSelectedExercises(prev => prev.map((ex, i) => {
      if (i !== index) return ex;
      
      if (field === 'set_type' && value === 'normal') {
        return { ...ex, [field]: value, linked_to_previous: false, series_plan: null };
      }

      if (field === 'set_type' && value === 'piramidale') {
        const serie = parseInt(ex.serie, 10) || 3;
        const reps = parseInt(ex.ripetizioni_max, 10) || 12;
        return {
          ...ex,
          set_type: value,
          series_plan: buildDefaultSeriesPlan(serie, reps, ex.peso_suggerito),
          linked_to_previous: false,
        };
      }

      if (field === 'set_type' && value !== 'piramidale') {
        return { ...ex, [field]: value, series_plan: null };
      }
      
      return { ...ex, [field]: value };
    }));
  };

  const removeExercise = (index) => {
    setSelectedExercises(prev => prev.filter((_, i) => i !== index));
  };

  // Gestione drag and drop
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setSelectedExercises(prev => {
        const oldIndex = prev.findIndex(item => item.id === active.id);
        const newIndex = prev.findIndex(item => item.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          return arrayMove(prev, oldIndex, newIndex);
        }
        return prev;
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || selectedExercises.length === 0) {
      toast.error('Nome template e almeno un esercizio sono richiesti');
      return;
    }

    setLoading(true);

    try {
      const data = {
        ...formData,
        exercises: selectedExercises.map((ex, index) => {
          const isCardio = ex.is_cardio === 1 || ex.is_cardio === true;
          
          // Un solo campo: ripetizioni_max
          // Per esercizi cardio: converti minuti in secondi per il database
          // Per esercizi normali: usa ripetizioni direttamente
          let ripetizioni_max = parseInt(ex.ripetizioni_max) || (isCardio ? 20 : 12);
          
          if (isCardio) {
            // Converti minuti in secondi per il database
            ripetizioni_max = ripetizioni_max * 60;
          }
          
          // Gestione note: salva come JSON stringa (formato: {"trainer": "...", "user": null, "system": null})
          let notesValue = null;
          if (ex.notes) {
            if (typeof ex.notes === 'object' && ex.notes !== null) {
              // Se è già un oggetto, convertilo in JSON stringa
              notesValue = JSON.stringify(ex.notes);
            } else if (typeof ex.notes === 'string' && ex.notes.trim() !== '') {
              // Se è una stringa, potrebbe essere già JSON o testo semplice
              // Se inizia con '{', è già JSON
              if (ex.notes.trim().startsWith('{')) {
                notesValue = ex.notes.trim();
              } else {
                // Altrimenti, convertilo in formato JSON trainer
                notesValue = JSON.stringify({ trainer: ex.notes.trim(), user: null, system: null });
              }
            }
          }
          
          const repsPayload = buildTemplateRepsPayload(ex, ripetizioni_max);
          
          return {
            esercizio_id: catalogIdFromRow(ex),
            order_index: index,
            serie: parseInt(ex.serie) || (isCardio ? 1 : 3),
            ripetizioni_min: repsPayload.ripetizioni_min,
            ripetizioni_max: repsPayload.ripetizioni_max,
            peso_suggerito: isCardio ? 0 : (parseFloat(ex.peso_suggerito) || 0),
            tempo_recupero: (() => {
              const value = parseInt(ex.tempo_recupero);
              return isNaN(value) ? 90 : value;
            })(),
            set_type: (ex.set_type && ex.set_type.trim() !== '') ? ex.set_type.trim() : 'normal',
            series_plan: ex.set_type === 'piramidale' ? ex.series_plan : null,
            linked_to_previous: ex.linked_to_previous ? 1 : 0,
            is_isometric_scheda: ex.is_isometric_scheda ? 1 : 0, // WEBAPP-004: Campo isometrico
            notes: notesValue
          };
        })
      };

      const response = await api.put(`/gym_workout_templates.php?action=update&id=${template.id}`, data);

      if (response.data.success) {
        onSuccess();
      }
    } catch (error) {
      console.error('Errore nell\'aggiornamento template:', error);
      toast.error('Errore nell\'aggiornamento del template');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 ${isFullscreen ? 'z-[9999]' : 'z-50'}`}>
      <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden flex flex-col ${
        isFullscreen ? 'w-full h-full max-w-full max-h-full' : 'w-[90vw] h-[90vh]'
      }`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              ✏️ {t('gym.membersManager.templateModal.editTemplate')}
            </h2>
            
            {/* WEBAPP-GYM-004: Bottoni fullscreen e chiudi */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 text-white/80 hover:text-white transition-colors"
                title={isFullscreen ? 'Esci da schermo intero' : 'Modalità schermo intero'}
              >
                {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
              </button>
              <button
                onClick={onClose}
                className="p-2 text-white/80 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {loadingData ? (
          <div className="flex-1 p-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-600 dark:text-white/65">Caricamento...</span>
          </div>
        ) : (
          <>
            {/* Tabs e Pulsanti Azione */}
            <div className="border-b border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-gray-900/50 flex-shrink-0">
              <div className="flex items-center justify-between px-6">
                <nav className="flex space-x-4" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === 'info'
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-white/65'
                  }`}
                >
                    {t('gym.membersManager.templateModal.infoTab')}
                  </button>
                  <button
                    onClick={() => setActiveTab('exercises')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'exercises'
                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-white/65'
                    }`}
                  >
                    {t('gym.membersManager.templateModal.exercisesTab')}
                </button>
                  <button
                    onClick={() => setActiveTab('template')}
                    className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'template'
                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-white/65'
                    }`}
                  >
                    {t('gym.membersManager.templateModal.templateTab')} ({selectedExercises.length})
                </button>
              </nav>
                
                {/* Pulsanti Azione */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm text-gray-700 dark:text-white/85 hover:bg-gray-200 dark:hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {t('gym.membersManager.templateModal.cancel')}
                  </button>
                  
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex items-center px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('common.saving')}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {t('gym.membersManager.templateModal.saveChanges')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 p-6 overflow-hidden flex flex-col">
              {activeTab === 'info' ? (
                <div className="space-y-4 overflow-y-auto">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                      {t('gym.membersManager.templateModal.templateName')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                      Descrizione
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                        Difficoltà
                      </label>
                      <select
                        name="difficulty_level"
                        value={formData.difficulty_level}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
                      >
                        <option value="beginner">Principiante</option>
                        <option value="intermediate">Intermedio</option>
                        <option value="advanced">Avanzato</option>
                        <option value="expert">Esperto</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                        Obiettivo
                      </label>
                      <select
                        name="target_goal"
                        value={formData.target_goal}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
                      >
                        <option value="general">Generale</option>
                        <option value="strength">Forza</option>
                        <option value="hypertrophy">Ipertrofia</option>
                        <option value="endurance">Resistenza</option>
                        <option value="flexibility">Flessibilità</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                        Durata Stimata (min)
                      </label>
                      <input
                        type="number"
                        name="estimated_duration"
                        value={formData.estimated_duration}
                        onChange={handleChange}
                        min="15"
                        max="180"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-white/85 mb-2">
                      Note Interne
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                </div>
              ) : activeTab === 'template' ? (
                <div className="flex flex-col flex-1 min-h-0">
                  {/* Tab Template: Solo esercizi selezionati */}
                  <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                    <div className="flex items-center justify-between mb-3 flex-shrink-0">
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {t('gym.membersManager.templateModal.selectedExercises')} ({selectedExercises.length})
                      </h3>
                      {selectedExercises.length > 0 && (
                        <button
                          onClick={() => setSelectedExercises([])}
                          className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 flex items-center gap-1"
                        >
                          <Trash2 className="h-3 w-3" />
                          Rimuovi Tutti
                        </button>
                      )}
                    </div>
                    
                  {selectedExercises.length === 0 ? (
                      <div className="flex-1 flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg min-h-0">
                        <div className="text-center">
                          <div className="text-gray-400 dark:text-gray-500 mb-2">
                            <FileText className="h-8 w-8 mx-auto" />
                          </div>
                          <p className="text-sm text-gray-500 dark:text-white/65">
                          Nessun esercizio selezionato
                        </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {t('gym.membersManager.templateModal.goToExercisesTab')}
                          </p>
                      </div>
                    </div>
                  ) : (
                      <div className="flex-1 min-h-0 overflow-hidden">
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={selectedExercises.map((ex) => ex.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="h-full overflow-y-auto space-y-3" style={{ overscrollBehavior: 'contain' }}>
                              {selectedExercises.map((exercise, index) => (
                                <SortableExerciseItem
                                  key={exercise.id}
                                  exercise={exercise}
                                  index={index}
                                  onUpdate={updateExercise}
                                  onRemove={removeExercise}
                                  selectedExercises={selectedExercises}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col flex-1 min-h-0">
                  {/* Tab Esercizi: Lista completa degli esercizi */}
                  <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                    <div className="mb-3 flex-shrink-0">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                        {t('gym.membersManager.templateModal.selectExercises')}
                        </h3>
                      <p className="text-sm text-gray-600 dark:text-white/65">
                        {t('gym.membersManager.templateModal.selectExercisesDescription')}
                      </p>
                    </div>
                    <div className="flex-1 min-h-0 overflow-hidden">
                          <NewExerciseSelector
                            searchTerm={searchTerm}
                            exercises={availableExercises}
                            selectedExercises={selectedExercises}
                            onSelectExercise={handleSelectExercise}
                            onShowAddExerciseForm={() => setShowAddExerciseForm(true)}
                            loading={loadingExercises || initialLoading}
                            hasMore={hasMore}
                            onLoadMore={loadMore}
                            onFiltersChange={handleFiltersChange}
                            onSearchChange={handleSearchChange}
                            gymExerciseSelector
                          />
                        </div>
                      </div>
                </div>
              )}
            </div>

            {/* Modal Crea Nuovo Esercizio */}
            {showAddExerciseForm && (
              <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                <div className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[#dad8e5] bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
                  <div className="flex shrink-0 items-center justify-between border-b border-[#dad8e5] bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
                    <h3 className="text-lg font-semibold text-[#3a3a3a] dark:font-open-sans dark:text-white">
                      {t('gym.membersManager.templateModal.createNewExercise')}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowAddExerciseForm(false)}
                      className="rounded-lg p-2 text-[#3a3a3a] transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
                    >
                      <X size={24} />
                    </button>
                  </div>
                  <div className="max-h-[calc(85vh-80px)] overflow-y-auto bg-[#faf9fc] p-6 dark:bg-gray-900">
                    <ImprovedUserExerciseForm
                      embedded
                      onClose={() => setShowAddExerciseForm(false)}
                      onSuccess={handleExerciseCreated}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Footer rimosso - pulsanti spostati sopra */}
          </>
        )}
      </div>
    </div>
  );
}

